/* global JSX */
import ComputerIcon from "@mui/icons-material/Computer";
import DoNotDisturbOffIcon from "@mui/icons-material/DoNotDisturbOff";
import ListAltIcon from "@mui/icons-material/ListAlt";
import PhoneAndroidIcon from "@mui/icons-material/PhoneAndroid";
import PersonOffIcon from "@mui/icons-material/PersonOff";
import MicrosoftIcon from "@mui/icons-material/Microsoft";
import { Box, Grid, Tooltip, Typography } from "@mui/material";
import * as React from "react";
import { AvatarImage } from "ui/Scripts/source/components/framework/Avatar/AvatarImage";
import { Base } from "ui/Scripts/source/framework/base";
import { EnumHelper, RecordEmailNotificationType, UserType } from "ui/Scripts/source/models/enums";
import { Translations } from "ui/Scripts/source/models/translations";
import { AppUtils } from "../../../framework/appUtils";
import { infiniteListPageSize } from "../../../models/common/webConsts";
import * as userService from "../../../models/services/userService";
import { ISessionState } from "../../../models/session/sessionState";
import { IUserListItem } from "../../../models/user/userListItem";
import { IUserListItems } from "../../../models/user/userListItems";
import { ActionContainer } from "../../framework/ActionContainer";
import { AddButton } from "../../framework/Button/AddButton";
import { SettingsButton } from "../../framework/Button/SettingsButton";
import { GridRow } from "../../framework/Grid/GridRow";
import { IListUsingComponentState, List } from "../../framework/List/List";
import { ListActionButtons } from "../../framework/List/ListActionButtons";
import { ListCardField } from "../../framework/List/ListCardField";
import { MainListContainer } from "source/components/framework/List/MainListContainer";
import { ListDetailsButton } from "../../framework/List/ListDetailsButton";
import { ListLinkValue } from "../../framework/List/ListLinkValue";
import { ListSearchContainer } from "../../framework/List/ListSearchContainer";
import { ListSearchFilter } from "../../framework/List/ListSearchFilter";
import { ListToggleButtons } from "../../framework/List/ListToggleButtons";
import { PageTitle } from "../../framework/Page/PageTitle";
import { UserDialogMenuItem, UserEditDialog } from "../UserEditDialog/UserEditDialog";
import { IPageNavMenuItemActionsAction } from "source/components/framework/PageNavMenuItem/Actions";
import { ToolButtonDropdown } from "ui/Scripts/source/components/framework/Button/ToolButtonDropdown";
import { AppMoreIcon } from "source/components/framework/Icon/AppMoreIcon";
import { ImportUsersDialog } from "../ImportUsersDialog";
import { AppAddIcon } from "source/components/framework/Icon/AppAddIcon";
import { AppQrCodeIcon } from "source/components/framework/Icon/AppQrCodeIcon";

// UserList
export interface IUserListProp {
    filterName: string;
    sessionState: ISessionState;
    companyId: string;
    disableGlobalLoading?: boolean;
    preloaded?: boolean;
    waitingPreloadedData?: boolean;
    list?: IUserListItems;
    listFilterIsSet: boolean;
    listFilterIsQrCodeFilter: boolean;
    userGroupIds?: string[];
    activityState?: string;
    sortColumn: string;
    sortOrderIsAsc: boolean;
    dataId?: string;
    startDateStr?: string;
    endDateStr?: string;
    titleSuffix?: JSX.Element;
    reportTabs: React.ReactNode;
    settingsActions: IPageNavMenuItemActionsAction[];
    onRefreshSessionState: () => void;
    onSetSortData: (sortColumn: string, sortOrderIsAsc: boolean) => void;
    onOpenFiltersDialog: () => void;
    onToggleSettingsDialog: () => void;
    onChangeToQrCodeFilter?: () => void;
}

interface IUserListState extends IListUsingComponentState<IUserListItem> {
    // List
    intialized: boolean;
    isLoading: boolean;
    waitingPreloadedData: boolean;
    pageSize: number;
    page: number;
    filter: string;
    userGroupIds: string[];
    activityState: number;
    sortColumn: string;
    sortOrderIsAsc: boolean;
    listModeIsLine: boolean;
    checkedIds: string[];
    companyCanUseInstantMessages: boolean

    // Dialog
    editDialogUserType: UserType;
    editDialogDefaultMenuItem: UserDialogMenuItem;
    editDialogChangeToQrCodeFilter: boolean;

    // ImportUsersDialog
    showImportUsersDialog: boolean;
}

export class UserList extends React.Component<IUserListProp, IUserListState> {
    constructor(props: IUserListProp) {
        super(props);
        this.state = {
            intialized: props.list && !!props.preloaded,
            isLoading: false,
            waitingPreloadedData: !Base.isNullOrUndefined(props.waitingPreloadedData) ? props.waitingPreloadedData : (props.list ? false : props.preloaded),
            page: props.list ? props.list.page : 1,
            hasMore: props.list ? props.list.hasMore : true,
            filter: "",
            userGroupIds: props.userGroupIds ? props.userGroupIds.filter(i => i !== Base.emptyGuid) : [],
            activityState: props.activityState ? parseInt(props.activityState, 10) : 0,
            pageSize: props.list ? props.list.pageSize : infiniteListPageSize,
            sortColumn: props.sortColumn,
            sortOrderIsAsc: props.sortOrderIsAsc,
            listModeIsLine: true,
            items: props.list ? props.list.items : [],
            companyCanUseInstantMessages: props.list ? props.list.companyCanUseInstantMessages : false,
            selectedId: null,
            checkedIds: [],
            // Details
            detailsItem: null,
            // Dialog
            showEditDialog: false,
            editDialogId: "",
            editDialogHasPrevious: false,
            editDialogHasNext: false,
            editDialogUserType: UserType.Normal,
            editDialogDefaultMenuItem: UserDialogMenuItem.General,
            editDialogChangeToQrCodeFilter: false,
            // ImportUsersDialog
            showImportUsersDialog: false
        };
    }

    searchItems = async (companyId: string, pageSize: number, page: number, filter: string, userGroupIds: string[], activityState: number, sortColumn: string, sortOrderIsAsc: boolean,
        resetItems: boolean, refreshList: boolean, moveNext: boolean = false, setUserDialogData: boolean = true) => {
        const state = this.state;
        this.setState({ isLoading: true });
        const result = await AppUtils.callService(() => userService.getUserListItems(companyId, !refreshList ? pageSize : (page * pageSize), !refreshList ? page : 1, filter, userGroupIds, activityState, sortColumn, sortOrderIsAsc),
            false, this.props.disableGlobalLoading, () => this.setState({ isLoading: false }));
        if (!result) return;
        const items = List.getListItems(state.items, state.selectedId, state.checkedIds, result.items, resetItems, refreshList);
        let selectedId = items.selectedId;
        let index = items.items.findIndex(i => i.id === selectedId);
        let detailsItem = index > -1 ? items.items[index] : null;
        if (moveNext && detailsItem && index < items.items.length - 1) {
            index = index + 1;
            detailsItem = items.items[index];
            selectedId = items.items[index].id;
        }
        if (state.editDialogChangeToQrCodeFilter) {
            setUserDialogData = false;
        }
        this.setState({
            intialized: true,
            editDialogChangeToQrCodeFilter: false,
            page: !refreshList ? result.page : page,
            hasMore: result.hasMore,
            filter,
            userGroupIds,
            activityState,
            sortColumn,
            sortOrderIsAsc,
            items: items.items,
            companyCanUseInstantMessages: result.companyCanUseInstantMessages,
            selectedId,
            checkedIds: items.checkedIds,
            detailsItem,
            editDialogDefaultMenuItem: setUserDialogData ? UserDialogMenuItem.General : this.state.editDialogDefaultMenuItem,
            editDialogId: setUserDialogData ? (moveNext && detailsItem ? detailsItem.id : "") : this.state.editDialogId,
            editDialogHasPrevious: setUserDialogData ? index > 0 : this.state.editDialogHasPrevious,
            editDialogHasNext: setUserDialogData ? (index < items.items.length - 1 || result.hasMore) : this.state.editDialogHasNext,
        });
        this.props.onSetSortData(sortColumn, sortOrderIsAsc);
    };

    refreshList = async (setUserDialogData: boolean = true) => {
        const state = this.state;
        await this.searchItems(this.props.companyId, state.pageSize, state.page, state.filter, state.userGroupIds, state.activityState, state.sortColumn, state.sortOrderIsAsc, false, true, false, setUserDialogData);
    };

    componentDidMount(): void {
        const state = this.state;
        if (this.props.preloaded) return;
        this.searchItems(this.props.companyId, state.pageSize, state.page, state.filter, state.userGroupIds, state.activityState, state.sortColumn, state.sortOrderIsAsc, false, false);
    }

    componentDidUpdate(prevProps: IUserListProp, prevState: IUserListState) {
        const props = this.props;
        const state = this.state;
        if (state.waitingPreloadedData) {
            if (!props.list || (props.dataId ?? "") === (prevProps.dataId ?? "")) return;
            this.setState({
                intialized: true,
                waitingPreloadedData: false,
                page: props.list.page,
                hasMore: props.list.hasMore,
                items: props.list.items,
                pageSize: props.list.pageSize,
                sortColumn: props.list.sortColumn,
                sortOrderIsAsc: props.list.sortOrderIsAsc,
                companyCanUseInstantMessages: props.list.companyCanUseInstantMessages,
            });
        } else {
            const prevDataId = prevProps.dataId ? prevProps.dataId : "";
            const dataId = props.dataId ? props.dataId : "";
            const prevCompanyId = prevProps.companyId ? prevProps.companyId : "";
            const companyId = props.companyId ? props.companyId : "";
            const prevUserGroupIds = prevProps.userGroupIds ? prevProps.userGroupIds.filter(i => i !== Base.emptyGuid) : [];
            const userGroupIds = props.userGroupIds ? props.userGroupIds.filter(i => i !== Base.emptyGuid) : [];
            const prevActivityState = prevProps.activityState ? parseInt(prevProps.activityState, 10) : 0;
            const activityState = props.activityState ? parseInt(props.activityState, 10) : 0;
            const prevSortColumn = prevProps.sortColumn;
            const sortColumn = props.sortColumn;
            const prevSortOrderIsAsc = prevProps.sortOrderIsAsc;
            const sortOrderIsAsc = props.sortOrderIsAsc;
            if (dataId === prevDataId && companyId === prevCompanyId &&
                Base.equalStrArrays(prevUserGroupIds, userGroupIds) && prevActivityState === activityState &&
                prevSortColumn === sortColumn && prevSortOrderIsAsc === sortOrderIsAsc) return;
            this.searchItems(companyId, state.pageSize, state.page, state.filter, userGroupIds, activityState, sortColumn, sortOrderIsAsc, true, false);
        }
    }

    changeFilter = (filter: string) => {
        const state = this.state;
        this.searchItems(this.props.companyId, state.pageSize, 1, filter, state.userGroupIds, state.activityState, state.sortColumn, state.sortOrderIsAsc, true, false);
    };

    handleSearchLines = (sortColumn: string, sortOrderIsAsc: boolean, nextPage: boolean) => {
        const state = this.state;
        this.searchItems(this.props.companyId, state.pageSize, !nextPage ? 1 : state.page + 1, state.filter, state.userGroupIds, state.activityState, sortColumn, sortOrderIsAsc, !nextPage, false);
    };

    handleSetSelectedIds = (selectedId: string, checkedIds: string[]) => {
        this.setState({
            selectedId,
            checkedIds,
            detailsItem: this.state.items.find(i => i.id === selectedId)
        });
    };
    
    handleListModeChange = (listModeIsLine: boolean) => {
        this.setState({
            listModeIsLine,
        });
    };

    handleListModeToggle = (_event: React.MouseEvent<HTMLElement>, listModeIsLine: boolean) => {
        this.setState({
            listModeIsLine,
        });
    };

    handleAdd = () => {
        this.setState({ ...List.getShowEditDialogStateChange(this.state, Base.emptyGuid), editDialogDefaultMenuItem: UserDialogMenuItem.General, editDialogUserType: UserType.Normal });
    };

    handleAddQrCodeUser = () => {
        this.setState({ ...List.getShowEditDialogStateChange(this.state, Base.emptyGuid), editDialogDefaultMenuItem: UserDialogMenuItem.General, editDialogUserType: UserType.QrCode });
    };

    handleOpenLine = (id: string) => {
        this.setState({ ...List.getShowEditDialogStateChange(this.state, id), editDialogDefaultMenuItem: UserDialogMenuItem.General, editDialogUserType: UserType.Normal });
    };

    handleUserDialogOk = () => {
        this.refreshList(false);
    };

    handleUserDialogCancel = (refreshList: boolean) => {
        this.setState({
            showEditDialog: false
        });
        if (!refreshList) return;
        this.refreshList(false);
    };

    handleUserDialogNewSaved = (newUserId: string, _userCode: string, savedIsQrCodeAccount: boolean) => {
        const editDialogChangeToQrCodeFilter = savedIsQrCodeAccount && !this.props.listFilterIsQrCodeFilter && !!this.props.onChangeToQrCodeFilter;
        this.setState({
            showEditDialog: true,
            editDialogId: newUserId,
            editDialogHasPrevious: false,
            editDialogHasNext: false,
            editDialogDefaultMenuItem: UserDialogMenuItem.UserGroups,
            editDialogChangeToQrCodeFilter,
        });
        if (editDialogChangeToQrCodeFilter) {
            this.props.onChangeToQrCodeFilter();
        } else {
            this.refreshList(false);
        }
    };

    setEditDialogItemByIndex = (index: number) => {
        const state = this.state;
        this.setState(List.getEditDialogItemByIndexStateChange(this.state, index, () => this.searchItems(this.props.companyId, state.pageSize, state.page + 1, state.filter, state.userGroupIds, state.activityState, state.sortColumn, state.sortOrderIsAsc, false, false, true)));
    };

    // #region Additional functions: Import Users
    handleImportUsers = () => {
        this.setState({
            showImportUsersDialog: true
        });
    };

    handleImportUsersDialogOk = () => {
        this.setState({
            showImportUsersDialog: false
        });
        this.refreshList(false);
    };

    handleImportUsersCancel = () => {
        this.setState({
            showImportUsersDialog: false
        });
    };
    // #endregion Additional functions: Import Users

    render() {
        const props = this.props;
        const state = this.state;
        
        return (
            <>
                <PageTitle title={Translations.Users + (props.filterName ? " - " + props.filterName : (props.listFilterIsSet || state.filter ? " - " + Translations.Filtered : ""))}>
                    {props.settingsActions.length > 0 &&
                        <SettingsButton onClick={props.onToggleSettingsDialog} sx={{ marginRight: "1.5rem" }}>
                            {Translations.Settings}
                        </SettingsButton>
                    }
                    <AddButton
                        onClick={this.handleAdd}
                        actions={[
                            { title: Translations.AddUser, icon: <AppAddIcon />, onClick: this.handleAdd },
                            { title: Translations.AddQrCodeUser, icon: <AppQrCodeIcon />, onClick: this.handleAddQrCodeUser }
                        ]}>
                        {Translations.Add}
                    </AddButton>
                </PageTitle>
                <MainListContainer>
                    <ActionContainer
                        margin="1rem .5rem 1rem 1rem"
                        leftItems={
                            props.reportTabs
                        }
                        rightItems={
                            <>
                                <ListActionButtons
                                    filterIsSet={props.listFilterIsSet}
                                    onFilter={props.onOpenFiltersDialog}
                                />
                                <ListToggleButtons value={state.listModeIsLine} onChange={this.handleListModeToggle} />
                                <ToolButtonDropdown
                                    variant="icon"
                                    actions={[
                                        { title: Translations.ImportUsers, onClick: this.handleImportUsers },
                                    ]}
                                    icon={<AppMoreIcon />}
                                    tooltip={Translations.AdditionalFunctions}
                                    menuRight={true}
                                />

                            </>
                        }
                    />
                    <ListSearchContainer>
                        <ListSearchFilter
                            searchFilter={state.filter}
                            onSearchClick={this.changeFilter} />
                    </ListSearchContainer>
                    <List
                        intialized={state.intialized}
                        isLoading={state.isLoading}
                        hasMore={state.hasMore}
                        globalLoadingInUse={!props.disableGlobalLoading}
                        height="calc(100vh - 296px)" //Main List
                        header={state.listModeIsLine
                            ? {
                                columns: [
                                    { size: 3, column: "code", content: Translations.UserName },
                                    { size: 3, column: "name", content: Translations.Name },
                                    { size: 4, column: "groups", content: Translations.UserGroups },
                                    { size: 2, column: "endDate", content: Translations.UserEndDate }
                                ]
                            }
                            : null}
                        lines={state.listModeIsLine
                            ? state.items.map((item) => {
                                return {
                                    columns: [
                                        {
                                            size: 3,
                                            column: "code",
                                            content:
                                            <Box display="flex" justifyContent="space-between" alignItems="center" flexGrow={2}>
                                                <Box display="flex" sx={{ overflow: "hidden" }}>
                                                    <AvatarImage
                                                        id={item.code}
                                                        name={item.getName()}
                                                        image={item.picture}
                                                        tooltip={item.getName()}
                                                    />
                                                    <Box display="flex" sx={{ marginLeft: ".5rem", overflow: "hidden" }}>
                                                        <ListLinkValue onClick={() => this.handleOpenLine(item.id)}>{item.code}</ListLinkValue>
                                                    </Box>
                                                </Box>
                                                <Box display="flex">
                                                    {item.microsoftAccount &&
                                                        <Tooltip title={Translations.MicrosoftAccount}>
                                                            <MicrosoftIcon sx={{ marginLeft: ".2rem" }} />
                                                        </Tooltip>
                                                    }
                                                    {item.mobileInUse &&
                                                        <Tooltip title={item.mobileActive ? Translations.UserIsActiveInMobile : Translations.UserHasUsedMobile}>
                                                            <PhoneAndroidIcon sx={{ marginLeft: ".2rem" }} color={item.mobileActive ? "success" : undefined} />
                                                        </Tooltip>
                                                    }
                                                    {item.webActive &&
                                                        <Tooltip title={Translations.UserIsActiveInWeb}>
                                                            <ComputerIcon sx={{ marginLeft: ".2rem" }} color="success" />
                                                        </Tooltip>
                                                    }
                                                    {item.hasRecordUser &&
                                                        <Tooltip title={item.hasActiveRecordUser ? Translations.RecordUser : Translations.RecordUserHasBeenArchived}>
                                                            <ListAltIcon sx={(theme) => ({ marginLeft: ".2rem", color: !item.hasActiveRecordUser ? "rgba(0, 0, 0, 0.3)" : undefined })}/>
                                                        </Tooltip>
                                                    }
                                                    {!item.hasLoggedIn &&
                                                        <Tooltip title={Translations.UserHasNotLoggedIn}>
                                                            <PersonOffIcon sx={{ marginLeft: ".2rem", color: "error.light" }} />
                                                        </Tooltip>
                                                    }
                                                    {(item.emailNotificationType === 0 || !item.email) &&
                                                        <Tooltip title={Translations.UserCannotRecieveEmailNotifications}>
                                                            <DoNotDisturbOffIcon sx={{ marginLeft: ".2rem", color: "error.light" }} />
                                                        </Tooltip>
                                                    }
                                                </Box>
                                            </Box>
                                        },
                                        { size: 3, column: "name", content: item.getName() },
                                        { size: 4, column: "groups", content: item.userGroupsStr },
                                        { size: 2, column: "endDate", content: Base.timeToDateStr(item.endDate) }
                                    ],
                                    id: item.id,
                                    classes: item.disabled ? "disabled" : ""
                                };
                            })
                            : null
                        }
                        cards={!state.listModeIsLine
                            ? state.items.map((item) => {
                                return {
                                    //classes: (item.disabled ? " disabled" : ""),
                                    sm: 4,
                                    rows: [
                                        {
                                            cols: [
                                                {
                                                    size: 6,
                                                    title: Translations.Code,
                                                    value:
                                                    <Box display="flex" alignItems="center" sx={{ overflow: "hidden" }}>
                                                        <AvatarImage
                                                            id={item.code}
                                                            name={item.getName()}
                                                            image={item.picture}
                                                            tooltip={item.getName()}
                                                        />
                                                        <Box display="flex" sx={{ marginLeft: ".5rem", overflow: "hidden" }}>
                                                            <ListLinkValue title={item.code} onClick={() => this.handleOpenLine(item.id)}>{item.code}</ListLinkValue>
                                                        </Box>
                                                    </Box>
                                                },
                                                { size: 6, title: Translations.Name, value: item.getName() }
                                            ]
                                        },
                                        {
                                            cols: [
                                                { size: 6, title: Translations.EmailAddress, value: item.email },
                                                { size: 6, title: Translations.UserEndDate, value: Base.timeToDateStr(item.endDate) }
                                            ]
                                        },
                                        {
                                            cols: [
                                                { title: Translations.UserGroups, value: item.userGroupsStr }
                                            ]
                                        }],
                                    id: item.id
                                };
                            })
                            : null
                        }
                        lineDetails={state.detailsItem
                            ? <Box position="relative">
                                <GridRow>
                                    <Grid item sm={4}>
                                        <GridRow>
                                            <Grid item sm={12}>
                                                <ListCardField title={Translations.UserName} value={state.detailsItem.code} />
                                            </Grid>
                                        </GridRow>
                                        <GridRow>
                                            <Grid item sm={12}>
                                                <ListCardField title={Translations.Name} value={state.detailsItem.getName()} />
                                            </Grid>
                                        </GridRow>
                                        <GridRow>
                                            <Grid item sm={12}>
                                                <ListCardField title={Translations.EmailAddress} value={state.detailsItem.email} />
                                            </Grid>
                                        </GridRow>
                                        <GridRow>
                                            <Grid item sm={6}>
                                                <ListCardField title={Translations.DefaultLanguage} value={state.detailsItem.defaultCulture} />
                                            </Grid>
                                            <Grid item sm={6}>
                                                <ListCardField title={Translations.UserEndDate} value={Base.timeToDateStr(state.detailsItem.endDate)} />
                                            </Grid>
                                        </GridRow>
                                    </Grid>
                                    <Grid item sm={4}>
                                        <GridRow>
                                            <Grid item sm={12}>
                                                <ListCardField title={Translations.Description} value={state.detailsItem.comment} />
                                            </Grid>
                                        </GridRow>
                                    </Grid>
                                    <Grid item sm={4}>
                                        <GridRow>
                                            <Grid item sm={12}>
                                                <ListCardField title={Translations.UserGroups} value={state.detailsItem.userGroupsStr} />
                                            </Grid>
                                        </GridRow>
                                        <GridRow>
                                            <Grid item sm={12}>
                                                <ListCardField title={Translations.EmailNotificationInUse} value={(state.detailsItem.emailNotificationType > 0 ? Translations.Yes : Translations.No)} />
                                                <ListCardField title={Translations.RecordEmailNotificationType} value={
                                                    <Typography>{(state.detailsItem.recordEmailNotificationType > 0 ? Translations.Yes : Translations.No) +
                                                        (!EnumHelper.isEqual(state.detailsItem.recordEmailNotificationType, RecordEmailNotificationType.NotInUse)
                                                            ? ", " + (EnumHelper.isEqual(state.detailsItem.recordEmailNotificationType, RecordEmailNotificationType.Expiration090)
                                                                ? Translations.ExpiringIn90Days
                                                                : (EnumHelper.isEqual(state.detailsItem.recordEmailNotificationType, RecordEmailNotificationType.Expiration120)
                                                                    ? Translations.ExpiringIn120Days
                                                                    : Translations.ExpiringIn180Days))
                                                            : "")}
                                                    </Typography>
                                                } />
                                                {!EnumHelper.isEqual(state.detailsItem.recordEmailNotificationType, RecordEmailNotificationType.NotInUse) &&
                                                    <ListCardField title={Translations.RecordEmailNotificationShowExpired} value={(state.detailsItem.recordEmailNotificationShowExpired ? Translations.Yes : Translations.No)} />
                                                }
                                                {state.companyCanUseInstantMessages &&
                                                    <ListCardField title={Translations.InstantMessageSettings} value={
                                                        EnumHelper.InstantMessageUserInvitationAcceptTypeToStr(state.detailsItem.instantMessageUserInvitationAcceptType)
                                                    } />
                                                }
                                            </Grid>
                                        </GridRow>
                                    </Grid>
                                </GridRow>
                                <ListDetailsButton
                                    onClick={() => this.handleOpenLine(state.selectedId)}
                                />
                            </Box>
                            : null}
                        sortColumn={state.sortColumn}
                        sortOrderIsAsc={state.sortOrderIsAsc}
                        selectedId={state.selectedId}
                        checkedIds={state.checkedIds}
                        onSetSelectedIds={this.handleSetSelectedIds}
                        onSearchLines={this.handleSearchLines}
                        onOpenLine={this.handleOpenLine}
                    />
                </MainListContainer>
                {(state.showEditDialog && !!state.editDialogId) &&
                    <UserEditDialog
                        sessionState={props.sessionState}
                        companyId={props.companyId}
                        userId={state.editDialogId}
                        userType={state.editDialogUserType}
                        defaultMenuItem={state.editDialogDefaultMenuItem}
                        hasPrevious={state.editDialogHasPrevious}
                        hasNext={state.editDialogHasNext}
                        onSaved={this.handleUserDialogOk}
                        onClose={this.handleUserDialogCancel}
                        onRefreshList={() => this.refreshList(false)}
                        onNewSaved={this.handleUserDialogNewSaved}
                        onPrevious={() => List.moveToPrevious(state, this.setEditDialogItemByIndex)}
                        onNext={() => List.moveToNext(state, this.setEditDialogItemByIndex)}
                        onRefreshSessionState={props.onRefreshSessionState}
                    />
                }
                {state.showImportUsersDialog &&
                    <ImportUsersDialog
                        companyId={props.companyId}
                        onOk={this.handleImportUsersDialogOk}
                        onCancel={this.handleImportUsersCancel}
                    />
                }
            </>
        );
    }
}